import React from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Divider,
  Space,
  Table,
  Typography,
  Upload,
  InputNumber,
} from "antd";
import { PlusOutlined, SaveOutlined, InboxOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { formatCurrencyWithConversion } from "../../../utils/currencyUtils";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

const CreditFormLayout = ({
  form,
  isEditMode,
  loading,
  invoiceItems,
  setInvoiceItems,
  partners,
  columns,
  calculatedValues,
  onFinish,
  handlePartnerChange,
  addItem,
  navigate,
  selectedBrand,
  brands = [],
  allBrands = [],
  onBrandChange,
  onValuesChange,
  formTitle = "Create Credit",
  defaultCreditType = "bill",
  onSplitCredit,
}) => {
  // Get the current currency value from the form with a space after it for better formatting
  const getCurrencySymbol = () => (form.getFieldValue("currency") || "$") + " ";
  const getCurrentCurrency = () => form.getFieldValue("currency") || "$";

  if (!selectedBrand && brands.length === 0) {
    return (
      <Card
        title={`Create ${
          defaultCreditType === "bill"
            ? "Credit Note (Bill)"
            : "Credit Note (Invoice)"
        }`}
        bordered={false}
      >
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Title level={4}>Loading brands...</Title>
        </div>
      </Card>
    );
  }

  const resolvedTitle =
    formTitle || (isEditMode ? "Edit Credit Note" : "Create Credit Note");

  const resolvedSubmitLabel = isEditMode ? "Update Credit" : "Create Credit";

  return (
    <Card title={resolvedTitle}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        initialValues={{
          creditType: defaultCreditType || "bill",
          issue_date: dayjs(),
          currency: "$",
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="brand"
              label="Brand"
              rules={[{ required: true, message: "Please select a brand" }]}
            >
              <Select
                placeholder="Select a brand"
                onChange={onBrandChange}
                value={selectedBrand?.id}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {brands.map((brand) => (
                  <Option key={brand.id} value={brand.id}>
                    {brand.brand_name || brand.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="creditType"
              label="Type"
              rules={[
                {
                  required: true,
                  message: "Please select credit type",
                },
              ]}
            >
              <Select
                onChange={(value) => {
                  // Trigger onValuesChange immediately when creditType changes
                  if (onValuesChange) {
                    onValuesChange({ creditType: value });
                  }
                }}
              >
                <Option value="bill">Bill</Option>
                <Option value="invoice">Invoice</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="number"
              label="Credit Number"
              rules={[
                {
                  required: true,
                  message: "Please enter credit number",
                },
              ]}
            >
              <Input placeholder="e.g. CR-2025-001" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="currency"
              label="Currency"
              rules={[{ required: true, message: "Please select a currency" }]}
            >
              <Select
                placeholder="Select or type currency"
                allowClear
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                mode="combobox"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                <Select.Option value="$">$ - USD</Select.Option>
                <Select.Option value="€">€ - EUR</Select.Option>
                <Select.Option value="£">£ - GBP</Select.Option>
                <Select.Option value="¥">¥ - JPY</Select.Option>
                <Select.Option value="C$">C$ - CAD</Select.Option>
                <Select.Option value="A$">A$ - AUD</Select.Option>
                <Select.Option value="CHF">CHF - Swiss Franc</Select.Option>
                <Select.Option value="₹">₹ - INR</Select.Option>
                <Select.Option value="R$">R$ - BRL</Select.Option>
                <Select.Option value="₽">₽ - RUB</Select.Option>
                <Select.Option value="₩">₩ - KRW</Select.Option>
                <Select.Option value="₪">₪ - ILS</Select.Option>
                <Select.Option value="₦">₦ - NGN</Select.Option>
                <Select.Option value="₨">₨ - PKR</Select.Option>
                <Select.Option value="₫">₫ - VND</Select.Option>
                <Select.Option value="₱">₱ - PHP</Select.Option>
                <Select.Option value="₴">₴ - UAH</Select.Option>
                <Select.Option value="₸">₸ - KZT</Select.Option>
                <Select.Option value="₼">₼ - AZN</Select.Option>
                <Select.Option value="₾">₾ - GEL</Select.Option>
                <Select.Option value="AED">AED - UAE Dirham</Select.Option>
                <Select.Option value="SAR">SAR - Saudi Riyal</Select.Option>
                <Select.Option value="QAR">QAR - Qatari Riyal</Select.Option>
                <Select.Option value="KWD">KWD - Kuwaiti Dinar</Select.Option>
                <Select.Option value="BHD">BHD - Bahraini Dinar</Select.Option>
                <Select.Option value="OMR">OMR - Omani Rial</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="partner"
              label={
                <Space>
                  <span>Partner</span>
                  <Button
                    type="primary"
                    style={{
                      fontSize: "10px",
                      padding: "0 6px",
                      height: "20px",
                    }}
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      navigate("/accounting/settings", {
                        state: { activeTab: "3" },
                      })
                    }
                  >
                    Add
                  </Button>
                </Space>
              }
              rules={[{ required: true, message: "Please select a partner" }]}
            >
              <Select
                placeholder="Select a partner"
                onChange={handlePartnerChange}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {partners.map((partner) => (
                  <Option key={partner.id} value={partner.id}>
                    {partner.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="issue_date"
              label="Issue Date"
              rules={[{ required: true, message: "Please select issue date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Credit Items</Divider>

        <Button
          type="dashed"
          onClick={addItem}
          style={{ marginBottom: 16 }}
          icon={<PlusOutlined />}
        >
          Add Item
        </Button>

        <Table
          columns={columns}
          dataSource={invoiceItems}
          pagination={false}
          rowKey="key"
          size="small"
          expandable={{
            expandedRowRender: (record) => {
              // Initialize payment_terms array if it doesn't exist
              const itemPaymentTerms = record.payment_terms || [];
              
              // Get line number (1-based index)
              const lineNumber = invoiceItems.findIndex(item => item.key === record.key) + 1;
              
              // Get issue date from form
              const issueDateValue = form.getFieldValue("issueDate");
              const baseDate = issueDateValue ? dayjs(issueDateValue) : dayjs();
              
              // Calculate item subtotal for amount calculation
              const itemSubtotal = parseFloat(record.subtotal || 0);
              const currencySymbol = getCurrencySymbol();

              const addItemPaymentTerm = () => {
                // Calculate remaining percentage to distribute
                const totalPercentage = itemPaymentTerms.reduce((sum, term) => sum + parseFloat(term.percentage || 0), 0);
                const remainingPercentage = Math.max(0, 100 - totalPercentage);
                
                // Calculate due_days incrementing by 30 (30, 60, 90, 120, etc.)
                const maxDueDays = itemPaymentTerms.length > 0
                  ? Math.max(...itemPaymentTerms.map(term => parseFloat(term.due_days || 0)))
                  : 0;
                const nextDueDays = maxDueDays + 30;
                
                // If there are existing terms and remaining percentage is 0, split equally
                if (itemPaymentTerms.length > 0 && remainingPercentage === 0) {
                  // Distribute equally among all terms (including new one)
                  const totalTerms = itemPaymentTerms.length + 1;
                  const basePercentage = Math.floor(100 / totalTerms);
                  const remainder = 100 - (basePercentage * totalTerms);
                  
                  // Distribute base percentage to all existing terms
                  // Remainder will go to the last term (new term)
                  const updatedTerms = itemPaymentTerms.map((term) => ({
                    ...term,
                    percentage: basePercentage
                  }));
                  
                  // New term gets base percentage + all remainder
                  const newTerm = {
                    id: Date.now() + Math.random(),
                    percentage: basePercentage + remainder,
                    due_days: nextDueDays,
                  };
                  
                  const updatedItems = invoiceItems.map((item) =>
                    item.key === record.key
                      ? { ...item, payment_terms: [...updatedTerms, newTerm] }
                      : item
                  );
                  if (setInvoiceItems) {
                    setInvoiceItems(updatedItems);
                  }
                } else {
                  // Use remaining percentage or default to 100 if no terms exist
                  const newTerm = {
                    id: Date.now() + Math.random(),
                    percentage: remainingPercentage > 0 ? remainingPercentage : (itemPaymentTerms.length === 0 ? 100 : 0),
                    due_days: itemPaymentTerms.length === 0 ? 30 : nextDueDays,
                  };
                  const updatedItems = invoiceItems.map((item) =>
                    item.key === record.key
                      ? { ...item, payment_terms: [...itemPaymentTerms, newTerm] }
                      : item
                  );
                  if (setInvoiceItems) {
                    setInvoiceItems(updatedItems);
                  }
                }
              };

              const removeItemPaymentTerm = (termId) => {
                const updatedItems = invoiceItems.map((item) =>
                  item.key === record.key
                    ? { ...item, payment_terms: itemPaymentTerms.filter((t) => t.id !== termId) }
                    : item
                );
                if (setInvoiceItems) {
                  setInvoiceItems(updatedItems);
                }
              };

              const updateItemPaymentTerm = (termId, field, value) => {
                let updatedTerms;
                if (field === 'percentage') {
                  // When updating percentage, ensure total doesn't exceed 100
                  const currentTotal = itemPaymentTerms.reduce((sum, term) => {
                    if (term.id === termId) {
                      return sum + parseFloat(value || 0);
                    }
                    return sum + parseFloat(term.percentage || 0);
                  }, 0);
                  
                  // If total exceeds 100, cap the value
                  const otherTermsTotal = itemPaymentTerms
                    .filter(t => t.id !== termId)
                    .reduce((sum, term) => sum + parseFloat(term.percentage || 0), 0);
                  const maxAllowed = 100 - otherTermsTotal;
                  const cappedValue = Math.min(parseFloat(value || 0), Math.max(0, maxAllowed));
                  
                  updatedTerms = itemPaymentTerms.map((t) =>
                    t.id === termId ? { ...t, [field]: cappedValue } : t
                  );
                } else {
                  updatedTerms = itemPaymentTerms.map((t) =>
                    t.id === termId ? { ...t, [field]: value } : t
                  );
                }
                
                const updatedItems = invoiceItems.map((item) =>
                  item.key === record.key
                    ? { ...item, payment_terms: updatedTerms }
                    : item
                );
                if (setInvoiceItems) {
                  setInvoiceItems(updatedItems);
                }
              };

              const itemPaymentTermColumns = [
                {
                  title: "Percentage (%)",
                  dataIndex: "percentage",
                  key: "percentage",
                  render: (text, term) => {
                    const totalPercentage = itemPaymentTerms.reduce((sum, t) => sum + parseFloat(t.percentage || 0), 0);
                    const otherTermsTotal = itemPaymentTerms
                      .filter(t => t.id !== term.id)
                      .reduce((sum, t) => sum + parseFloat(t.percentage || 0), 0);
                    const maxAllowed = 100 - otherTermsTotal;
                    const currentValue = parseFloat(text || 0);
                    const isOverLimit = currentValue > maxAllowed;
                    
                    return (
                      <div>
                        <InputNumber
                          value={text}
                          min={0}
                          max={maxAllowed}
                          step={1}
                          precision={2}
                          onChange={(value) =>
                            updateItemPaymentTerm(term.id, "percentage", value)
                          }
                          formatter={(value) => {
                            if (!value) return "";
                            return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                          }}
                          parser={(value) => {
                            if (!value) return "";
                            return value.replace(/,/g, "");
                          }}
                          style={{ width: "100%" }}
                          status={isOverLimit ? "error" : ""}
                        />
                        {isOverLimit && (
                          <div style={{ fontSize: "10px", color: "#ff4d4f", marginTop: "2px" }}>
                            Max: {maxAllowed.toFixed(2)}%
                          </div>
                        )}
                        {totalPercentage > 100 && (
                          <div style={{ fontSize: "10px", color: "#ff4d4f", marginTop: "2px" }}>
                            Total exceeds 100%
                          </div>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: "Due Days",
                  dataIndex: "due_days",
                  key: "due_days",
                  render: (text, term) => (
                    <InputNumber
                      value={text}
                      min={0}
                      step={1}
                      precision={0}
                      onChange={(value) => updateItemPaymentTerm(term.id, "due_days", value !== null && value !== undefined ? value : 0)}
                      formatter={(value) => {
                        // Handle 0 explicitly - it's a valid value meaning "due today"
                        if (value === null || value === undefined || value === "") return "";
                        return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      }}
                      parser={(value) => {
                        // Handle empty string by returning 0 (due today)
                        if (value === null || value === undefined || value === "") return 0;
                        const parsed = value.replace(/,/g, "");
                        return parsed === "" ? 0 : Number(parsed);
                      }}
                      style={{ width: "100%" }}
                    />
                  ),
                },
                {
                  title: "Due Date",
                  key: "due_date",
                  render: (_, term) => {
                    const issueDate = form.getFieldValue("issue_date");
                    const baseDate = issueDate ? dayjs(issueDate) : dayjs();
                    const dueDays = term.due_days !== undefined && term.due_days !== null 
                      ? parseFloat(term.due_days) 
                      : 0;
                    // Ensure dueDays is a valid number
                    const validDueDays = isNaN(dueDays) ? 0 : Math.max(0, dueDays);
                    const dueDate = baseDate.add(validDueDays, "days").format("YYYY-MM-DD");
                    const displayText = validDueDays === 0 ? `${dueDate} (Due Today)` : dueDate;
                    return <span key={`item-due-date-${term.id}-${term.due_days}`}>{displayText}</span>;
                  },
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  render: (_, term) => {
                    const percentage = parseFloat(term.percentage || 0);
                    const amount = (itemSubtotal * percentage) / 100;
                    return (
                      <span>
                        {currencySymbol}
                        {amount.toFixed(getDecimalPrecision())}
                      </span>
                    );
                  },
                },
                {
                  title: "Action",
                  key: "action",
                  render: (_, term) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeItemPaymentTerm(term.id)}
                    />
                  ),
                },
              ];

              return (
                <div style={{ padding: "16px", background: "#fafafa" }}>
                  <Divider orientation="left">Payment Terms for Line {lineNumber}</Divider>
                  <Button
                    type="dashed"
                    onClick={addItemPaymentTerm}
                    style={{ marginBottom: 16 }}
                    icon={<PlusOutlined />}
                  >
                    Add Payment Term for Line {lineNumber}
                  </Button>
                  {itemPaymentTerms.length > 0 ? (
                    <Table
                      columns={itemPaymentTermColumns}
                      dataSource={itemPaymentTerms}
                      pagination={false}
                      rowKey="id"
                      size="small"
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                      No payment terms added yet. Click &quot;Add Payment Term for Line {lineNumber}&quot; to create payment terms.
                    </div>
                  )}
                </div>
              );
            },
            rowExpandable: () => true,
          }}
        />

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <div
              style={{
                padding: "8px 16px",
                background: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Row justify="space-between">
                <Col>Subtotal:</Col>
                <Col>
                  {formatCurrencyWithConversion(
                    calculatedValues.calculated_subtotal || 0,
                    getCurrentCurrency()
                  )}
                </Col>
              </Row>
              <Row justify="space-between">
                <Col>Tax:</Col>
                <Col>
                  {formatCurrencyWithConversion(
                    calculatedValues.calculated_tax_amount || 0,
                    getCurrentCurrency()
                  )}
                </Col>
              </Row>
              <Row justify="space-between">
                <Col>Discount:</Col>
                <Col>
                  {formatCurrencyWithConversion(
                    calculatedValues.calculated_discount_amount || 0,
                    getCurrentCurrency()
                  )}
                </Col>
              </Row>
              <Divider style={{ margin: "8px 0" }} />
              <Row justify="space-between">
                <Col>
                  <strong>Total:</strong>
                </Col>
                <Col>
                  <strong>
                    {formatCurrencyWithConversion(
                      calculatedValues.calculated_total || 0,
                      getCurrentCurrency()
                    )}
                  </strong>
                </Col>
              </Row>
            </div>
          </Col>

          {/* Editable form fields for manual adjustment */}
          <Col xs={24} sm={12} md={16}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="subtotal"
                  label="Subtotal"
                  rules={[{ required: false }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="0.00"
                    precision={2}
                    min={0}
                    step={0.01}
                    formatter={(value) => {
                      if (!value) return "";
                      const currency = form.getFieldValue("currency") || "$";
                      return `${currency} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }}
                    parser={(value) => {
                      if (!value) return "";
                      const currency = form.getFieldValue("currency") || "$";
                      return value.replace(
                        new RegExp(`${currency.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?|(,*)`, "g"),
                        ""
                      );
                    }}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="tax_rate"
                  label="Tax Rate (%)"
                  rules={[{ required: false }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="0.00"
                    precision={2}
                    min={0}
                    max={100}
                    step={0.01}
                    formatter={(value) => {
                      if (!value) return "";
                      return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }}
                    parser={(value) => {
                      if (!value) return "";
                      return value.replace(/,/g, "");
                    }}
                    addonAfter="%"
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="discount_amount"
                  label="Discount Amount"
                  rules={[{ required: false }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="0.00"
                    precision={2}
                    min={0}
                    step={0.01}
                    formatter={(value) => {
                      if (!value) return "";
                      const currency = form.getFieldValue("currency") || "$";
                      return `${currency} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }}
                    parser={(value) => {
                      if (!value) return "";
                      const currency = form.getFieldValue("currency") || "$";
                      return value.replace(
                        new RegExp(`${currency.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?|(,*)`, "g"),
                        ""
                      );
                    }}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="total_amount"
                  label="Total Amount"
                  rules={[{ required: false }]}
                >
                  <InputNumber
                    placeholder="0.00"
                    precision={2}
                    min={0}
                    step={0.01}
                    formatter={(value) => {
                      if (!value) return "";
                      const currency = form.getFieldValue("currency") || "$";
                      return `${currency} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }}
                    parser={(value) => {
                      if (!value) return "";
                      const currency = form.getFieldValue("currency") || "$";
                      return value.replace(
                        new RegExp(`${currency.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?|(,*)`, "g"),
                        ""
                      );
                    }}
                    readOnly
                    style={{ width: "100%", backgroundColor: "#f5f5f5" }}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Divider />

        <Form.Item name="notes" label="Notes">
          <TextArea rows={4} placeholder="Enter any additional notes here" />
        </Form.Item>

        {/* File URL display - separate from the upload component */}
        {form.getFieldValue("fileUrl") && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                marginBottom: "8px",
                background: "#f5f5f5",
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>Current File:</strong>
              </p>
              <a
                href={form.getFieldValue("fileUrl")}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#1890ff",
                }}
              >
                <svg
                  viewBox="64 64 896 896"
                  focusable="false"
                  data-icon="file-pdf"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ marginRight: "8px" }}
                >
                  <path d="M531.3 574.4l.3-1.4c5.8-23.9 13.1-53.7 7.4-80.7-3.8-21.3-19.5-29.6-32.9-30.2-15.8-.7-29.9 8.3-33.4 21.4-6.6 24-.7 56.8 10.1 98.6-13.6 32.4-35.3 79.5-51.2 107.5-29.6 15.3-69.3 38.9-75.2 68.7-1.2 5.5.2 12.5 3.5 18.8 3.7 7 9.6 12.4 16.5 15 3 1.1 6.6 2 10.8 2 17.6 0 46.1-14.2 84.1-79.4 5.8-1.9 11.8-3.9 17.6-5.9 27.2-9.2 55.4-18.8 80.9-23.1 28.2 15.1 60.3 24.8 82.1 24.8 21.6 0 30.1-12.8 33.3-20.5 5.6-13.5 2.9-30.5-6.2-39.6-13.2-13-45.3-16.4-95.3-10.2-24.6-15-40.7-35.4-52.4-65.8zM421.6 726.3c-13.9 20.2-24.4 30.3-30.1 34.7 6.7-12.3 19.8-25.3 30.1-34.7zm87.6-235.5c5.2 8.9 4.5 35.8.5 49.4-4.9-19.9-5.6-48.1-2.7-51.4.8.1 1.5.7 2.2 2zm-1.6 120.5c10.7 18.5 24.2 34.4 39.1 46.2-21.6 4.9-41.3 13-58.9 20.2-4.2 1.7-8.3 3.4-12.3 5 13.3-24.1 24.4-51.4 32.1-71.4zm155.6 65.5c.1.2.2.5-.4.9h-.2c-14.6.8-41.2-4.7-68.7-18.5 27.7-5.3 47.8-4.8 57.4-1.1 4.2 1.6 8.2 5 11.9 18.7z" />
                  <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326zm1.8 562H232V136h302v216a42 42 0 0042 42h216v494z" />
                </svg>
                {form.getFieldValue("fileUrl").split("/").pop()}
              </a>
            </div>
            <p style={{ marginTop: 8 }}>
              Upload a new file to replace the current one:
            </p>
          </div>
        )}

        {/* File upload component */}
        <Form.Item
          name="file"
          label="Upload Credit File"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e?.fileList;
          }}
        >
          <Upload.Dragger
            name="file"
            beforeUpload={() => false}
            maxCount={1}
            accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single file upload. Accepted formats: PDF, CSV,
              Excel, Images.
            </p>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
            >
              {resolvedSubmitLabel}
            </Button>
            {onSplitCredit && invoiceItems.length > 0 && (
              <Button
                type="default"
                onClick={onSplitCredit}
                loading={loading}
                style={{ 
                  borderColor: "#1890ff", 
                  color: "#1890ff",
                  fontWeight: "bold"
                }}
              >
                Split Credit
              </Button>
            )}
            <Button onClick={() => navigate("/accounting/credits")}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CreditFormLayout;
